import { FieldArray, Form, Formik, getIn } from 'formik';
import Modal from '~/components/common/Modal';
import {
  AddSpecialHours,
  AddHours,
  AddHoursDisableDelivery,
  AddSpecialHoursDisableDelivery,
} from '~/utils/schema/integration';
import { Field } from '~/components/common/Formik';
import {
  Box,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import themes from '~/themes';
import moment from 'moment';
import {
  GAP_TIME_OPTIONS,
  TIME_SLOT_OPTIONS,
  TIME_SLOT_TYPE,
} from '~/constants/stores';
import { debounce, isArray, isEmpty, isEqual, isString, sortBy } from 'lodash';
import { useGenTimeSlot, usePreviewGenTimeSlot } from '~/services/api/stores';
import { fromQueryString } from '~/utils/queryString';
import { useLocation } from 'react-router-dom';
import InfoIcon from '~/assets/images/icons/info-gray.svg';
import Tooltip from '~/components/common/Tooltip';
import Button from '~/components/common/Button';
import AddIcon from '~/assets/images/icons/add-black.svg';
import DeleteIcon from '~/assets/images/icons/delete-bin-line.png';
import { formatAmPm } from '~/utils/formatter';
import { HoursSelect } from './HoursSelect';

export type DeliveryHoursSelectItem = {
  weekdayValue: string;
  weekDayLabel: string;
  totalTimeSlot: number;
  available: boolean;
  name?: string;
  time_slot_generation: string;
  from_time: string;
  to_time: string;
  late_night: boolean;
  start_date?: string;
  end_date?: string;
  delivery_hours?: number;
  delivery_window?: number;
  next_available_time?: number;
  lead_time?: number;
  lead_time_type?: string;
  last_order_time?: string;
  time_slot?: Array<{
    from_time: string;
    to_time: string;
    lead_time?: number;
    lead_time_type?: string;
  }>;
};

interface DialogAddTimeSlotsProps {
  open: boolean;
  onClose: () => void;
  onSave: (val: any) => void;
  data?: Array<DeliveryHoursSelectItem>;
  isSpecial?: boolean;
  store: any;
  enableDelivery: boolean;
  invalidDays?: any;
  isBulkSelect?: boolean;
  editingIndex?: number; // Index of the special being edited, undefined if creating new
}

const defaultCustomizeTimeSlot = {
  from_time: '09:00',
  to_time: '21:00',
  lead_time: 0,
  lead_time_type: 'start_time',
};

export const DialogAddTimeSlotsHours: React.FC<DialogAddTimeSlotsProps> = ({
  open,
  onClose,
  onSave,
  data,
  isSpecial = false,
  store,
  enableDelivery,
  invalidDays,
  isBulkSelect = false,
  editingIndex,
}) => {
  const location = useLocation();
  const search = fromQueryString(location.search);
  const storeID = search.id as string;

  // Refs
  const formRef = useRef(null);
  const prevValues = useRef<any>();
  const prevAvailableRef = useRef<boolean | undefined>();
  const currentFormValuesRef = useRef<any>();

  // State
  const [dateError, setDateError] = useState('');
  const [timeSlotError, setTimeSlotError] = useState('');
  const [dirtyForm, setDirtyForm] = useState(false);
  const [previewTimeSlots, setPreviewTimeSlots] = useState<any>([]);
  const [currentTimeSlotGeneration, setCurrentTimeSlotGeneration] =
    useState('');
  const [hasFormikErrors, setHasFormikErrors] = useState(false);
  const [hasDateErrors, setHasDateErrors] = useState(false);
  const [availableChangeTrigger, setAvailableChangeTrigger] = useState(0);
  const [generateFields, setGenerateFields] = useState({
    from_time: '',
    to_time: '',
    late_night: false,
    delivery_window: 60,
    next_available_time: 60,
  });

  const { mutate: getTimeSlots, isLoading } = useGenTimeSlot({
    onSuccess: (res) => {
      const { delivery_hours, special_hours } = res;
      if (isSpecial) {
        onSave({
          ...data[0],
          ...prevValues.current,
          ...special_hours[0],
        });
      } else {
        onSave({
          ...data[0],
          ...prevValues.current,
          time_slot: delivery_hours[data[0].weekdayValue].time_slot,
        });
      }
      onClose();
    },
  });

  const { mutate: generateTimeSlots, isLoading: loadingPreview } =
    usePreviewGenTimeSlot({
      onSuccess: (res) => {
        const convertTimeSlots = (res || []).map(
          (slot) =>
            `${formatAmPm(
              moment(slot.from, 'HH:mm').format('h:mm a'),
            )} - ${formatAmPm(moment(slot.to, 'HH:mm').format('h:mm a'))}`,
        );
        setPreviewTimeSlots(convertTimeSlots);
      },
    });

  const formatTimeAmPm = useCallback(
    (time: string) => formatAmPm(moment(time, 'HH:mm').format('h:mm a')),
    [],
  );

  const getAdjacentWeekday = useCallback(
    (weekday: string, direction: 'prev' | 'next') => {
      const weekdays = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];
      const currentIndex = weekdays.indexOf(weekday.toLowerCase());
      if (currentIndex === -1) return null;

      if (direction === 'prev') {
        return weekdays[currentIndex === 0 ? 6 : currentIndex - 1];
      }
      return weekdays[currentIndex === 6 ? 0 : currentIndex + 1];
    },
    [],
  );

  const getWeekdayFromDate = useCallback((dateString: string) => {
    const weekdays = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    return weekdays[moment(dateString).day()];
  }, []);

  const getActiveSpecialDates = useCallback(() => {
    if (!store?.special_hours || !Array.isArray(store.special_hours)) {
      return [];
    }

    const today = moment().startOf('day');
    return store.special_hours.filter((special) => {
      if (!special.start_date || !special.end_date || !special.available) {
        return false;
      }
      const endDate = moment(special.end_date).endOf('day');
      return endDate.isSameOrAfter(today);
    });
  }, [store]);

  const getFutureSpecialDates = useCallback(() => {
    if (!store?.special_hours || !Array.isArray(store.special_hours)) {
      return [];
    }

    const today = moment().startOf('day');
    return store.special_hours.filter((special) => {
      if (!special.start_date || !special.end_date || !special.available) {
        return false;
      }
      // Include special dates that are still ongoing or in the future
      // Compare by day (not by time) to be consistent
      const endDate = moment(special.end_date).startOf('day');
      return endDate.isSameOrAfter(today);
    });
  }, [store]);

  // Validate date overlap for special hours
  const validateDateOverlap = useCallback(
    (specialValues: any) => {
      if (!isSpecial || !store?.special_hours) {
        return '';
      }

      if (!specialValues.start_date || !specialValues.end_date) {
        return '';
      }

      // Check for overlap with other special dates
      const currentStartDate = moment(specialValues.start_date);
      const currentEndDate = moment(specialValues.end_date);

      const hasOverlap = (store?.special_hours || []).some((special, index) => {
        if (!special.start_date || !special.end_date) {
          return false;
        }

        // Exclude current special being edited by index
        if (editingIndex !== undefined && index === editingIndex) {
          return false;
        }

        const specialStartDate = moment(special.start_date);
        const specialEndDate = moment(special.end_date);

        // Check if date ranges overlap
        // Two ranges overlap if: start1 <= end2 AND start2 <= end1
        return (
          currentStartDate.isSameOrBefore(specialEndDate, 'day') &&
          specialStartDate.isSameOrBefore(currentEndDate, 'day')
        );
      });

      if (hasOverlap) {
        return 'The selected start and end date overlaps with another scheduled special hours.';
      }

      return '';
    },
    [isSpecial, store, editingIndex],
  );

  const validateSpecialWithWeekdays = useCallback(
    (specialValues: any) => {
      if (!isSpecial || !store?.delivery_hours) {
        return '';
      }

      if (!specialValues.start_date || !specialValues.end_date) {
        return '';
      }

      // Skip validation if special date is not available
      if (specialValues.available === false) {
        return '';
      }

      // Validate start time < end time for AUTO mode same day
      // late_night can be: true (boolean - next day), false (boolean - same day), 'next_day' (string), 'same_day' (string)
      const isLateNight =
        specialValues.late_night === true ||
        specialValues.late_night === 'next_day';

      if (
        specialValues.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
        !isLateNight &&
        specialValues.from_time &&
        specialValues.to_time
      ) {
        if (
          (specialValues.from_time === '12:00' &&
            specialValues.to_time === '12:00') ||
          moment(specialValues.to_time, 'HH:mm').isSameOrBefore(
            moment(specialValues.from_time, 'HH:mm'),
          )
        ) {
          return 'Start time must be before end time';
        }
      }

      // For late_night with multiple days: end_time must be less than start_time
      // (since it goes into the next day)
      if (
        specialValues.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
        isLateNight &&
        specialValues.from_time &&
        specialValues.to_time
      ) {
        const startDate = moment(specialValues.start_date);
        const endDate = moment(specialValues.end_date);
        const isMultipleDays = !endDate.isSame(startDate, 'day');

        if (isMultipleDays) {
          // For multiple days with late_night, end_time must be strictly less than start_time
          // (end_time = start_time is OK since it means the same time next day)
          const startTime = moment(specialValues.from_time, 'HH:mm');
          const endTime = moment(specialValues.to_time, 'HH:mm');

          if (endTime.isAfter(startTime)) {
            return `End time (${formatTimeAmPm(
              specialValues.to_time,
            )}) must be before or equal to start time (${formatTimeAmPm(
              specialValues.from_time,
            )}) when spanning multiple days with late night mode`;
          }
        }
      }

      const currentDate = moment(specialValues.start_date);
      const endDate = moment(specialValues.end_date);
      const currentStartDate = moment(specialValues.start_date);
      const currentEndDate = moment(specialValues.end_date);

      // Get other special dates that are adjacent (right before or right after current special)
      const otherSpecials = (store?.special_hours || []).filter((special) => {
        if (!special.start_date || !special.end_date || !special.available) {
          return false;
        }

        // Exclude current special being edited
        if (
          special.start_date === specialValues.start_date &&
          special.end_date === specialValues.end_date &&
          special.name === specialValues.name
        ) {
          return false;
        }

        // Only include specials that are adjacent (end date + 1 day = current start OR current end + 1 day = special start)
        const specialEndDate = moment(special.end_date);
        const specialStartDate = moment(special.start_date);

        // Check if special ends right before current starts (previous adjacent)
        const dayAfterSpecialEnd = moment(specialEndDate).add(1, 'day');
        const isPrevAdjacent = dayAfterSpecialEnd.isSame(
          currentStartDate,
          'day',
        );

        // Check if current ends right before special starts (next adjacent)
        const dayAfterCurrentEnd = moment(currentEndDate).add(1, 'day');
        const isNextAdjacent = dayAfterCurrentEnd.isSame(
          specialStartDate,
          'day',
        );

        return isPrevAdjacent || isNextAdjacent;
      });

      // eslint-disable-next-line no-unmodified-loop-condition
      while (currentDate.isSameOrBefore(endDate)) {
        const prevDate = moment(currentDate).subtract(1, 'day');
        const prevDateWeekday = getWeekdayFromDate(
          prevDate.format('YYYY-MM-DD'),
        );

        // Check if previous day (weekday) has late_night that extends into this special date
        if (store.delivery_hours[prevDateWeekday]) {
          const prevWeekdayConfig = store.delivery_hours[prevDateWeekday];

          if (
            prevWeekdayConfig.available &&
            prevWeekdayConfig.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
            prevWeekdayConfig.late_night &&
            prevWeekdayConfig.to_time
          ) {
            // Previous weekday has late_night - check with special's start time
            let specialStartTime;

            if (specialValues.time_slot_generation === TIME_SLOT_TYPE.CUSTOM) {
              // For CUSTOM mode, check the time_slot array directly (not delivery_hours)
              if (
                specialValues.time_slot &&
                specialValues.time_slot.length > 0
              ) {
                const earliestSlot = specialValues.time_slot.reduce(
                  (earliest, slot) => {
                    const currentFrom = moment(slot.from_time, 'HH:mm');
                    const earliestFrom = moment(earliest.from_time, 'HH:mm');
                    return currentFrom.isBefore(earliestFrom) ? slot : earliest;
                  },
                  specialValues.time_slot[0],
                );
                specialStartTime = earliestSlot.from_time;
              }
            } else if (
              specialValues.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
              specialValues.from_time
            ) {
              specialStartTime = specialValues.from_time;
            }

            if (specialStartTime) {
              const weekdayToTime = moment(prevWeekdayConfig.to_time, 'HH:mm');
              const specialFromTime = moment(specialStartTime, 'HH:mm');

              if (weekdayToTime.isAfter(specialFromTime)) {
                return `Start time (${formatTimeAmPm(
                  specialStartTime,
                )}) conflicts with ${
                  prevDateWeekday.charAt(0).toUpperCase() +
                  prevDateWeekday.slice(1)
                }'s late night end time (${formatTimeAmPm(
                  prevWeekdayConfig.to_time,
                )}) on ${currentDate.format('MMM DD, YYYY')}`;
              }
            }
          }
        }

        // If special has late_night, check with next day weekday
        if (
          specialValues.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
          specialValues.late_night === 'next_day' &&
          specialValues.to_time
        ) {
          const nextDate = moment(currentDate).add(1, 'day');
          const nextDateWeekday = getWeekdayFromDate(
            nextDate.format('YYYY-MM-DD'),
          );

          // Check if the next day weekday has available hours
          if (store.delivery_hours[nextDateWeekday]) {
            const nextWeekdayConfig = store.delivery_hours[nextDateWeekday];

            if (nextWeekdayConfig.available) {
              let nextWeekdayStartTime;

              // Get next weekday's earliest start time
              if (
                nextWeekdayConfig.time_slot_generation ===
                  TIME_SLOT_TYPE.CUSTOM &&
                nextWeekdayConfig.time_slot?.length > 0
              ) {
                const earliestSlot = nextWeekdayConfig.time_slot.reduce(
                  (earliest, slot) => {
                    const currentFrom = moment(slot.from_time, 'HH:mm');
                    const earliestFrom = moment(earliest.from_time, 'HH:mm');
                    return currentFrom.isBefore(earliestFrom) ? slot : earliest;
                  },
                  nextWeekdayConfig.time_slot[0],
                );
                nextWeekdayStartTime = earliestSlot.from_time;
              } else if (
                nextWeekdayConfig.time_slot_generation ===
                  TIME_SLOT_TYPE.AUTO &&
                nextWeekdayConfig.from_time
              ) {
                nextWeekdayStartTime = nextWeekdayConfig.from_time;
              }

              if (nextWeekdayStartTime) {
                const specialToTime = moment(specialValues.to_time, 'HH:mm');
                const weekdayFromTime = moment(nextWeekdayStartTime, 'HH:mm');

                if (specialToTime.isAfter(weekdayFromTime)) {
                  return `End time (${formatTimeAmPm(
                    specialValues.to_time,
                  )}) conflicts with ${
                    nextDateWeekday.charAt(0).toUpperCase() +
                    nextDateWeekday.slice(1)
                  }'s start time (${formatTimeAmPm(
                    nextWeekdayStartTime,
                  )}) on ${nextDate.format('MMM DD, YYYY')}`;
                }
              }
            }
          }
        }

        // Check with other adjacent special dates on the previous day (with late_night)
        const prevDateSpecials = otherSpecials.filter((special) => {
          const specialStart = moment(special.start_date);
          const specialEnd = moment(special.end_date);
          return (
            prevDate.isSameOrAfter(specialStart) &&
            prevDate.isSameOrBefore(specialEnd)
          );
        });

        let prevSpecialErrorMessage = '';
        prevDateSpecials.some((prevSpecial: any) => {
          // Only check if previous special has late_night (AUTO mode)
          if (
            prevSpecial.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
            prevSpecial.late_night &&
            prevSpecial.to_time
          ) {
            // Get current special's start time
            let currentSpecialStartTime;

            if (specialValues.time_slot_generation === TIME_SLOT_TYPE.CUSTOM) {
              if (
                specialValues.time_slot &&
                specialValues.time_slot.length > 0
              ) {
                const earliestSlot = specialValues.time_slot.reduce(
                  (earliest, slot) => {
                    const currentFrom = moment(slot.from_time, 'HH:mm');
                    const earliestFrom = moment(earliest.from_time, 'HH:mm');
                    return currentFrom.isBefore(earliestFrom) ? slot : earliest;
                  },
                  specialValues.time_slot[0],
                );
                currentSpecialStartTime = earliestSlot.from_time;
              }
            } else if (
              specialValues.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
              specialValues.from_time
            ) {
              currentSpecialStartTime = specialValues.from_time;
            }

            if (currentSpecialStartTime) {
              const prevSpecialToTime = moment(prevSpecial.to_time, 'HH:mm');
              const currentSpecialFromTime = moment(
                currentSpecialStartTime,
                'HH:mm',
              );

              if (prevSpecialToTime.isAfter(currentSpecialFromTime)) {
                prevSpecialErrorMessage = `Start time (${formatTimeAmPm(
                  currentSpecialStartTime,
                )}) conflicts with special date "${
                  prevSpecial.name || 'Unnamed'
                }" (${prevDate.format(
                  'MMM DD, YYYY',
                )}) late night end time (${formatTimeAmPm(
                  prevSpecial.to_time,
                )}) on ${currentDate.format('MMM DD, YYYY')}`;
                return true;
              }
            }
          }
          return false;
        });

        if (prevSpecialErrorMessage) {
          return prevSpecialErrorMessage;
        }

        // Check with other adjacent special dates on the next day (if current has late_night)
        if (
          specialValues.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
          specialValues.late_night === 'next_day' &&
          specialValues.to_time
        ) {
          const nextDate = moment(currentDate).add(1, 'day');
          const nextDateStr = nextDate.format('YYYY-MM-DD');
          const nextDateSpecials = otherSpecials.filter((special) => {
            const specialStart = moment(special.start_date);
            const specialEnd = moment(special.end_date);
            return (
              nextDate.isSameOrAfter(specialStart) &&
              nextDate.isSameOrBefore(specialEnd)
            );
          });

          let nextSpecialErrorMessage = '';
          nextDateSpecials.some((nextSpecial: any) => {
            // Get next special's start time
            let nextSpecialStartTime;

            if (
              nextSpecial.time_slot_generation === TIME_SLOT_TYPE.CUSTOM &&
              nextSpecial.delivery_hours &&
              nextSpecial.delivery_hours[nextDateStr]?.time_slot?.length > 0
            ) {
              const earliestSlot = nextSpecial.delivery_hours[
                nextDateStr
              ].time_slot.reduce((earliest, slot) => {
                const currentFrom = moment(slot.from_time, 'HH:mm');
                const earliestFrom = moment(earliest.from_time, 'HH:mm');
                return currentFrom.isBefore(earliestFrom) ? slot : earliest;
              }, nextSpecial.delivery_hours[nextDateStr].time_slot[0]);
              nextSpecialStartTime = earliestSlot.from_time;
            } else if (
              nextSpecial.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
              nextSpecial.from_time
            ) {
              nextSpecialStartTime = nextSpecial.from_time;
            }

            if (nextSpecialStartTime) {
              const currentSpecialToTime = moment(
                specialValues.to_time,
                'HH:mm',
              );
              const nextSpecialFromTime = moment(nextSpecialStartTime, 'HH:mm');

              if (currentSpecialToTime.isAfter(nextSpecialFromTime)) {
                nextSpecialErrorMessage = `End time (${formatTimeAmPm(
                  specialValues.to_time,
                )}) conflicts with special date "${
                  nextSpecial.name || 'Unnamed'
                }" start time (${formatTimeAmPm(
                  nextSpecialStartTime,
                )}) on ${nextDate.format('MMM DD, YYYY')}`;
                return true;
              }
            }
            return false;
          });

          if (nextSpecialErrorMessage) {
            return nextSpecialErrorMessage;
          }
        }

        currentDate.add(1, 'day');
      }

      return '';
    },
    [
      isSpecial,
      store,
      getWeekdayFromDate,
      formatTimeAmPm,
      getActiveSpecialDates,
    ],
  );

  const validateCustomTimeSlotsWithAdjacentDays = useCallback(
    (timeSlots: any[]) => {
      if (
        !data?.length ||
        data.length !== 1 ||
        !store?.delivery_hours ||
        isSpecial
      ) {
        return '';
      }

      if (!timeSlots || timeSlots.length === 0) {
        return '';
      }

      const currentWeekday = data[0].weekdayValue.toLowerCase();
      const prevWeekday = getAdjacentWeekday(currentWeekday, 'prev');

      // Find earliest from_time in custom time slots
      const earliestSlot = timeSlots.reduce((earliest, slot) => {
        const currentFrom = moment(slot.from_time, 'HH:mm');
        const earliestFrom = moment(earliest.from_time, 'HH:mm');
        return currentFrom.isBefore(earliestFrom) ? slot : earliest;
      }, timeSlots[0]);

      // Check with previous day ONLY if previous day is AUTO with late_night = true AND available = true
      if (prevWeekday && store.delivery_hours[prevWeekday]) {
        const prevDay = store.delivery_hours[prevWeekday];

        // Only check if previous day is AUTO with late_night AND available is true (CUSTOM never has late_night)
        if (
          prevDay.available &&
          prevDay.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
          prevDay.late_night &&
          prevDay.to_time
        ) {
          const prevToTime = moment(prevDay.to_time, 'HH:mm');
          const currentEarliestFromTime = moment(
            earliestSlot.from_time,
            'HH:mm',
          );

          if (prevToTime.isAfter(currentEarliestFromTime)) {
            return `Earliest time slot (${formatTimeAmPm(
              earliestSlot.from_time,
            )}) conflicts with ${
              prevWeekday.charAt(0).toUpperCase() + prevWeekday.slice(1)
            }'s late night end time (${formatTimeAmPm(prevDay.to_time)})`;
          }
        }
      }

      // NEW: Also check with future special dates that have late_night
      const futureSpecials = getFutureSpecialDates();
      const specialConflictError = futureSpecials.reduce(
        (error, special: any) => {
          if (error) return error;

          if (
            special.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
            special.late_night &&
            special.to_time
          ) {
            const currentDate = moment(special.start_date);
            const endDate = moment(special.end_date);

            // eslint-disable-next-line no-unmodified-loop-condition
            while (currentDate.isSameOrBefore(endDate)) {
              const nextDate = moment(currentDate).add(1, 'day');
              const nextDateWeekday = getWeekdayFromDate(
                nextDate.format('YYYY-MM-DD'),
              );

              // Check if next day after special is current weekday being edited
              if (nextDateWeekday === currentWeekday) {
                const specialToTime = moment(special.to_time, 'HH:mm');
                const currentEarliestFromTime = moment(
                  earliestSlot.from_time,
                  'HH:mm',
                );

                if (specialToTime.isAfter(currentEarliestFromTime)) {
                  return `Earliest time slot (${formatTimeAmPm(
                    earliestSlot.from_time,
                  )}) conflicts with special date "${
                    special.name || 'Unnamed'
                  }" (${currentDate.format(
                    'MMM DD',
                  )}) late night end time (${formatTimeAmPm(special.to_time)})`;
                }
              }

              currentDate.add(1, 'day');
            }
          }
          return '';
        },
        '',
      );

      if (specialConflictError) {
        return specialConflictError;
      }

      return '';
    },
    [
      data,
      store,
      isSpecial,
      getAdjacentWeekday,
      formatTimeAmPm,
      getFutureSpecialDates,
      getWeekdayFromDate,
    ],
  );

  const debouncedGenerateTimeSlots = useCallback(
    debounce((params) => {
      generateTimeSlots(params);
    }, 500),
    [],
  );

  const handleSetErrorHours = (rangeTime: {
    from: string;
    to: string;
    late_night?: string;
  }) => {
    const isNextDay = rangeTime.late_night === 'next_day';

    // Only validate if same day (not next day)
    if (!isNextDay) {
      if (
        (rangeTime.from === '12:00' && rangeTime.to === '12:00') ||
        moment(rangeTime.to, 'hh:mm').isSameOrBefore(
          moment(rangeTime.from, 'hh:mm'),
        )
      ) {
        setTimeSlotError('Start time must be before end time');
        return;
      }
    }

    // NEW: Validate for weekday editing - check against future special dates with late_night
    if (!isSpecial && store?.delivery_hours && data?.length >= 1) {
      const futureSpecials = getFutureSpecialDates();

      // Check if any future special date with late_night conflicts with current weekday edit
      if (futureSpecials.length > 0) {
        const selectedWeekdays = data.map((d) => d.weekdayValue.toLowerCase());

        const specialConflict = futureSpecials.some((special: any) => {
          // Only check special dates that are AUTO with late_night
          if (
            special.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
            special.late_night &&
            special.to_time
          ) {
            // Generate all dates in special range to check which weekdays they fall on
            const currentDate = moment(special.start_date);
            const endDate = moment(special.end_date);

            // eslint-disable-next-line no-unmodified-loop-condition
            while (currentDate.isSameOrBefore(endDate)) {
              const nextDate = moment(currentDate).add(1, 'day');
              const nextDateWeekday = getWeekdayFromDate(
                nextDate.format('YYYY-MM-DD'),
              );

              // Check if the next day after this special date is in our selected weekdays
              if (selectedWeekdays.includes(nextDateWeekday)) {
                // This special date's late_night will extend into the selected weekday
                const specialToTime = moment(special.to_time, 'HH:mm');
                const weekdayFromTime = moment(rangeTime.from, 'HH:mm');

                if (specialToTime.isAfter(weekdayFromTime)) {
                  setTimeSlotError(
                    `Start time conflicts with special date "${
                      special.name || 'Unnamed'
                    }" (${moment(currentDate).format(
                      'MMM DD',
                    )}) late night end time (${formatTimeAmPm(
                      special.to_time,
                    )})`,
                  );
                  return true;
                }
              }

              currentDate.add(1, 'day');
            }
          }
          return false;
        });

        if (specialConflict) {
          return;
        }
      }

      // Also check when editing weekday with late_night - validate with next day special dates
      if (isNextDay && futureSpecials.length > 0) {
        const selectedWeekdays = data.map((d) => d.weekdayValue.toLowerCase());

        // For each future special, check if any date in the special falls right after a selected weekday
        const lateNightConflict = futureSpecials.some((special: any) => {
          const specialStart = moment(special.start_date);
          const specialEnd = moment(special.end_date);
          const currentDate = moment(specialStart);

          // eslint-disable-next-line no-unmodified-loop-condition
          while (currentDate.isSameOrBefore(specialEnd)) {
            const currentDateStr = currentDate.format('YYYY-MM-DD');
            const prevDate = moment(currentDate).subtract(1, 'day');
            const prevDateWeekday = getWeekdayFromDate(
              prevDate.format('YYYY-MM-DD'),
            );

            // Check if the previous day is one of the selected weekdays we're editing
            if (selectedWeekdays.includes(prevDateWeekday)) {
              // The weekday we're editing with late_night will extend into this special date
              let specialStartTime;

              if (
                special.time_slot_generation === TIME_SLOT_TYPE.CUSTOM &&
                special.delivery_hours &&
                special.delivery_hours[currentDateStr]?.time_slot?.length > 0
              ) {
                const earliestSlot = special.delivery_hours[
                  currentDateStr
                ].time_slot.reduce((earliest, slot) => {
                  const currentFrom = moment(slot.from_time, 'HH:mm');
                  const earliestFrom = moment(earliest.from_time, 'HH:mm');
                  return currentFrom.isBefore(earliestFrom) ? slot : earliest;
                }, special.delivery_hours[currentDateStr].time_slot[0]);
                specialStartTime = earliestSlot.from_time;
              } else if (
                special.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
                special.from_time
              ) {
                specialStartTime = special.from_time;
              }

              if (specialStartTime) {
                const weekdayToTime = moment(rangeTime.to, 'HH:mm');
                const specialFromTime = moment(specialStartTime, 'HH:mm');

                if (weekdayToTime.isAfter(specialFromTime)) {
                  setTimeSlotError(
                    `End time (${formatTimeAmPm(
                      rangeTime.to,
                    )}) conflicts with special date "${
                      special.name || 'Unnamed'
                    }" start time (${formatTimeAmPm(
                      specialStartTime,
                    )}) on ${currentDate.format('MMM DD, YYYY')}`,
                  );
                  return true;
                }
              }
            }

            currentDate.add(1, 'day');
          }
          return false;
        });

        if (lateNightConflict) {
          return;
        }
      }
    }

    // Validate for bulk select or single day
    if (!isSpecial && store?.delivery_hours && data?.length >= 1) {
      // Case 1: Bulk select all 7 days - validate that same from/to won't cause overlap
      if (isBulkSelect && data.length === 7) {
        // When selecting all 7 days with late_night, validate circular overlap
        if (isNextDay) {
          const currentToTime = moment(rangeTime.to, 'HH:mm');
          const nextFromTime = moment(rangeTime.from, 'HH:mm');

          if (currentToTime.isAfter(nextFromTime)) {
            setTimeSlotError(
              `End time (${formatTimeAmPm(
                rangeTime.to,
              )}) conflicts with start time (${formatTimeAmPm(
                rangeTime.from,
              )}) when applied to all days with late night mode`,
            );
            return;
          }
        }
      }
      // Case 2: Bulk select less than 7 days or single day - validate with adjacent days
      else if (data.length >= 1) {
        const weekdays = [
          'sunday',
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
        ];
        const selectedWeekdays = data.map((d) => d.weekdayValue.toLowerCase());

        // For each selected day, check with adjacent days
        const hasConflict = selectedWeekdays.some((currentWeekday) => {
          const prevWeekday = getAdjacentWeekday(currentWeekday, 'prev');
          const nextWeekday = getAdjacentWeekday(currentWeekday, 'next');

          // Check previous day (if not in selected list)
          if (
            prevWeekday &&
            !selectedWeekdays.includes(prevWeekday) &&
            store.delivery_hours[prevWeekday]
          ) {
            const prevDay = store.delivery_hours[prevWeekday];
            // Only check if previous day is AUTO with late_night AND available is true
            if (
              prevDay.available &&
              prevDay.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
              prevDay.late_night &&
              prevDay.to_time
            ) {
              const prevToTime = moment(prevDay.to_time, 'HH:mm');
              const currentFromTime = moment(rangeTime.from, 'HH:mm');

              if (prevToTime.isAfter(currentFromTime)) {
                setTimeSlotError(
                  `Start time conflicts with ${
                    prevWeekday.charAt(0).toUpperCase() + prevWeekday.slice(1)
                  }'s late night end time (${formatTimeAmPm(prevDay.to_time)})`,
                );
                return true;
              }
            }
          }

          // Check next day (if not in selected list) - only for AUTO with late_night
          if (
            isNextDay &&
            nextWeekday &&
            !selectedWeekdays.includes(nextWeekday) &&
            store.delivery_hours[nextWeekday]
          ) {
            const nextDay = store.delivery_hours[nextWeekday];

            // Only check if next day has available = true
            if (nextDay.available) {
              let nextStartTime;

              // Get next day's earliest start time based on its type
              if (
                nextDay.time_slot_generation === TIME_SLOT_TYPE.CUSTOM &&
                nextDay.time_slot?.length > 0
              ) {
                // Next day is CUSTOM - find earliest from_time
                const earliestSlot = nextDay.time_slot.reduce(
                  (earliest, slot) => {
                    const currentFrom = moment(slot.from_time, 'HH:mm');
                    const earliestFrom = moment(earliest.from_time, 'HH:mm');
                    return currentFrom.isBefore(earliestFrom) ? slot : earliest;
                  },
                  nextDay.time_slot[0],
                );
                nextStartTime = earliestSlot.from_time;
              } else if (
                nextDay.time_slot_generation === TIME_SLOT_TYPE.AUTO &&
                nextDay.from_time
              ) {
                // Next day is AUTO - get from_time
                nextStartTime = nextDay.from_time;
              }

              if (nextStartTime) {
                const currentToTime = moment(rangeTime.to, 'HH:mm');
                const nextFromTime = moment(nextStartTime, 'HH:mm');

                if (currentToTime.isAfter(nextFromTime)) {
                  setTimeSlotError(
                    `End time conflicts with ${
                      nextWeekday.charAt(0).toUpperCase() + nextWeekday.slice(1)
                    }'s earliest start time (${formatTimeAmPm(nextStartTime)})`,
                  );
                  return true;
                }
              }
            }
          }

          return false;
        });

        if (hasConflict) {
          return;
        }

        // If bulk selecting consecutive days with late_night, check internal consistency
        if (isBulkSelect && isNextDay && selectedWeekdays.length > 1) {
          const sortedWeekdays = weekdays.filter((wd) =>
            selectedWeekdays.includes(wd),
          );

          // Check if selected days form a consecutive group
          const isConsecutive = sortedWeekdays.every((wd, idx) => {
            if (idx === sortedWeekdays.length - 1) return true;
            const nextWd = getAdjacentWeekday(wd, 'next');
            return nextWd === sortedWeekdays[idx + 1];
          });

          if (isConsecutive) {
            const currentToTime = moment(rangeTime.to, 'HH:mm');
            const nextFromTime = moment(rangeTime.from, 'HH:mm');

            if (currentToTime.isAfter(nextFromTime)) {
              setTimeSlotError(
                `End time (${formatTimeAmPm(
                  rangeTime.to,
                )}) conflicts with start time (${formatTimeAmPm(
                  rangeTime.from,
                )}) for consecutive selected days with late night mode`,
              );
              return;
            }
          }
        }
      }
    }

    setTimeSlotError('');
  };

  useEffect(() => {
    const {
      from_time,
      to_time,
      delivery_window,
      next_available_time,
      late_night,
    } = generateFields;

    if (from_time && to_time && delivery_window && next_available_time) {
      const startMoment = moment(from_time, 'HH:mm');
      const endMoment = moment(to_time, 'HH:mm');

      if (endMoment.isAfter(startMoment) || late_night) {
        debouncedGenerateTimeSlots({
          from_time,
          to_time,
          late_night,
          delivery_window,
          next_available_time,
        });
      }
    }
  }, [generateFields, debouncedGenerateTimeSlots]);

  useEffect(
    () => () => {
      debouncedGenerateTimeSlots.cancel();
    },
    [debouncedGenerateTimeSlots],
  );

  // Validate custom time slots on mount and when data/store changes
  useEffect(() => {
    if (
      data?.length === 1 &&
      !isSpecial &&
      data[0].time_slot_generation === TIME_SLOT_TYPE.CUSTOM &&
      data[0].time_slot
    ) {
      const error = validateCustomTimeSlotsWithAdjacentDays(data[0].time_slot);
      setTimeSlotError(error);
    }
  }, [data, store, validateCustomTimeSlotsWithAdjacentDays]);

  // Validate special dates with weekdays on mount
  useEffect(() => {
    if (
      isSpecial &&
      data?.length === 1 &&
      data[0].start_date &&
      data[0].end_date
    ) {
      // Validate date overlap first
      const dateOverlapError = validateDateOverlap(data[0]);
      setDateError(dateOverlapError);

      // For AUTO mode, need from_time
      // For CUSTOM mode, need time_slot
      const hasRequiredData =
        (data[0].time_slot_generation === TIME_SLOT_TYPE.AUTO &&
          data[0].from_time) ||
        (data[0].time_slot_generation === TIME_SLOT_TYPE.CUSTOM &&
          data[0].time_slot?.length > 0);

      if (hasRequiredData) {
        const error = validateSpecialWithWeekdays(data[0]);
        setTimeSlotError(error);
      }
    }
  }, [
    data,
    store,
    isSpecial,
    validateSpecialWithWeekdays,
    validateDateOverlap,
  ]);

  // Trigger validation when available changes from false to true
  useEffect(() => {
    if (availableChangeTrigger > 0 && currentFormValuesRef.current) {
      const values = currentFormValuesRef.current;
      if (values.time_slot_generation === TIME_SLOT_TYPE.AUTO) {
        if (isSpecial) {
          // Validate date overlap
          const dateOverlapError = validateDateOverlap(values);
          setDateError(dateOverlapError);
          // Validate time conflicts
          const error = validateSpecialWithWeekdays(values);
          setTimeSlotError(error);
        } else {
          handleSetErrorHours({
            from: values.from_time,
            to: values.to_time,
            late_night: values.late_night,
          });
        }
      } else if (
        values.time_slot_generation === TIME_SLOT_TYPE.CUSTOM &&
        values.time_slot
      ) {
        const error = validateCustomTimeSlotsWithAdjacentDays(values.time_slot);
        setTimeSlotError(error);
      }
    }
  }, [
    availableChangeTrigger,
    validateCustomTimeSlotsWithAdjacentDays,
    isSpecial,
    validateSpecialWithWeekdays,
    validateDateOverlap,
    handleSetErrorHours,
  ]);

  const migrateDeliveryHours = (startDate, endDate, time_slot) => {
    const dateObject = {};
    const currentDate = moment(startDate);

    while (currentDate.isSameOrBefore(endDate)) {
      dateObject[currentDate.format('YYYY-MM-DD')] = {
        time_slot,
      };
      currentDate.add(1, 'day');
    }

    return dateObject;
  };

  const onSubmit = (values) => {
    // Convert late_night to boolean if it exists
    const convertedValues = {
      ...values,
      ...(values.late_night !== undefined && {
        late_night: values.late_night === 'next_day',
      }),
    };

    if (
      !values.available ||
      !enableDelivery ||
      values.time_slot_generation === TIME_SLOT_TYPE.NONE ||
      (values.time_slot_generation === TIME_SLOT_TYPE.CUSTOM && !isSpecial)
    ) {
      onSave({ ...data[0], ...convertedValues });
      return onClose();
    }

    if (values.time_slot_generation === TIME_SLOT_TYPE.CUSTOM && isSpecial) {
      const delivery_hours = migrateDeliveryHours(
        values.start_date,
        values.end_date,
        values.time_slot,
      );
      onSave({ ...data[0], ...convertedValues, delivery_hours });
      return onClose();
    }

    prevValues.current = convertedValues;
    const newStore = { ...store };
    if (isSpecial) {
      if (!convertedValues.start_date || !convertedValues.end_date) {
        return setDateError('Please enter Start Date or End Date');
      }

      convertedValues.start_date = moment(convertedValues.start_date)
        .local()
        .format('YYYY-MM-DD');
      convertedValues.end_date = moment(convertedValues.end_date)
        .local()
        .format('YYYY-MM-DD');
      newStore.special_hours = [{ ...convertedValues }];
    } else {
      data?.forEach((day) => {
        newStore.delivery_hours[day.weekdayValue] = {
          ...day,
          ...convertedValues,
        };
      });
    }

    return getTimeSlots({ ...newStore, id: storeID });
  };

  const initialValues = useMemo(() => {
    if (enableDelivery) {
      if (isEmpty(data) || data?.length > 1)
        return {
          available: true,
          time_slot_generation: 'auto',
          start_date: '',
          end_date: '',
          late_night: 'same_day',
          delivery_window: 60,
          next_available_time: 60,
          from_time: '09:00',
          to_time: '21:00',
          lead_time: 0,
          lead_time_type: 'start_time',
          name: '',
          last_order_time: '23:59',
          time_slot: [defaultCustomizeTimeSlot],
        };
      const timeSlotSelected = data[0];
      const {
        available,
        time_slot_generation,
        start_date,
        end_date,
        late_night,
        delivery_window,
        next_available_time,
        lead_time,
        lead_time_type = 'start_time',
        from_time,
        to_time,
        name,
        last_order_time,
        time_slot,
        delivery_hours,
      } = timeSlotSelected;
      if (isSpecial) {
        return {
          available,
          time_slot_generation: time_slot_generation || 'auto',
          start_date,
          end_date,
          delivery_window,
          next_available_time,
          lead_time,
          lead_time_type,
          from_time,
          to_time,
          name,
          last_order_time: last_order_time || '23:59',
          time_slot:
            time_slot_generation === TIME_SLOT_TYPE.CUSTOM &&
            !isEmpty(delivery_hours[start_date]?.time_slot)
              ? sortBy(delivery_hours[start_date]?.time_slot, 'from_time')
              : [defaultCustomizeTimeSlot],
          late_night: late_night ? 'next_day' : 'same_day',
        };
      }
      return {
        available,
        time_slot_generation,
        delivery_window,
        next_available_time,
        lead_time,
        lead_time_type,
        from_time,
        to_time,
        last_order_time: last_order_time || '23:59',
        time_slot:
          time_slot_generation === TIME_SLOT_TYPE.CUSTOM && !isEmpty(time_slot)
            ? sortBy(time_slot, 'from_time')
            : [defaultCustomizeTimeSlot],
        late_night: late_night ? 'next_day' : 'same_day',
      };
    }

    if (isEmpty(data) || data?.length > 1) {
      return isSpecial
        ? {
            name: '',
            start_date: '',
            end_date: '',
            available: true,
            last_order_time: '23:59',
          }
        : {
            available: true,
            last_order_time: '23:59',
          };
    }
    const timeSlotSelected = data[0];
    const { available, last_order_time, name, start_date, end_date } =
      timeSlotSelected;
    if (isSpecial) {
      return {
        name,
        start_date,
        end_date,
        available,
        last_order_time: last_order_time || '23:59',
      };
    }
    return {
      available,
      last_order_time: last_order_time || '23:59',
    };
  }, [data, isSpecial, enableDelivery]);

  const title = useMemo(() => {
    if (isSpecial) {
      return isEmpty(data) ? 'Add Special Hours' : data[0]?.name;
    }
    return data?.map((day) => day?.weekDayLabel)?.join(', ') || '';
  }, [data]);

  const validationSchema = useMemo(() => {
    if (!enableDelivery) {
      return isSpecial
        ? AddSpecialHoursDisableDelivery
        : AddHoursDisableDelivery;
    }
    return isSpecial ? AddSpecialHours : AddHours;
  }, [isSpecial, enableDelivery]);

  const renderDisableDelivery = ({
    values,
    setFieldValue,
    errors: formErrors,
    touched,
  }) => (
    <>
      <Stack flexDirection='column' gap={1}>
        <Typography color={themes.color.violet500}>Availability</Typography>
        <Stack flexDirection='row' alignItems='center'>
          <Field.SwitchButton noText name='available' />
          Allow same day service during the following hours. Orders placed
          outside the hours will be processed in the next available day.
        </Stack>
      </Stack>
      {values.available && (
        <Stack mt={1.6}>
          <HoursSelect
            legend='Last Order Time'
            timeGap={15}
            value={values.last_order_time}
            onChange={(e: any) => {
              const { value: last_order_time } = e.target;
              setFieldValue('last_order_time', last_order_time);
            }}
            error={
              !!getIn(touched, 'last_order_time') &&
              !!getIn(formErrors, 'last_order_time')
            }
            helperText={
              getIn(touched, 'last_order_time') &&
              getIn(formErrors, 'last_order_time')
            }
          />
        </Stack>
      )}
    </>
  );

  const renderPreview = () => (
    <Stack
      flexDirection='column'
      sx={{
        width: '35%',
        px: 1.6,
        pt: 1.3,
        pb: 0,
        borderLeft: '1px solid #E2E7F0',
        [themes.breakpoints.down('md')]: {
          width: 'unset',
        },
      }}
    >
      <Typography
        sx={{
          color: 'rgba(51, 54, 61, 0.40)',
        }}
      >
        Preview
      </Typography>
      <Box
        sx={{
          backgroundColor: '#edf0f7',
          borderRadius: '4px 4px 0 0',
          overflowY: loadingPreview ? 'hidden' : 'auto',
          mt: 0.6,
          height: '100%',
          maxHeight: '390px',
          position: 'relative',
        }}
        className='customized-scrollbar'
      >
        {loadingPreview ? (
          <Stack
            sx={{
              width: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 999,
            }}
          >
            <CircularProgress size={30} />
          </Stack>
        ) : (
          previewTimeSlots?.map((slot, index) => (
            <Typography
              // eslint-disable-next-line react/no-array-index-key
              key={`${slot}-${index}`}
              sx={{
                textAlign: 'center',
                padding: '4px 8px',
              }}
            >
              {slot}
            </Typography>
          ))
        )}
      </Box>
    </Stack>
  );

  const renderGenerate = ({
    values,
    setFieldValue,
    errors: formErrors,
    touched,
  }) => {
    if (
      !isEqual(
        {
          from_time: values.from_time,
          to_time: values.to_time,
          late_night: values.late_night === 'next_day',
          delivery_window: values.delivery_window,
          next_available_time: values.next_available_time,
        },
        generateFields,
      )
    )
      setGenerateFields({
        from_time: values.from_time,
        to_time: values.to_time,
        late_night: values.late_night === 'next_day',
        delivery_window: values.delivery_window,
        next_available_time: values.next_available_time,
      });

    return (
      <>
        <Stack
          flexDirection='row'
          sx={{
            background: themes.bg.lightPurple,
            borderRadius: '4px',
            mt: 1.6,
            [themes.breakpoints.down('md')]: {
              flexDirection: 'column',
            },
          }}
        >
          <Stack
            flexDirection='column'
            gap={1.6}
            sx={{
              width: '65%',
              p: 1.6,
              [themes.breakpoints.down('md')]: {
                width: 'unset',
              },
            }}
          >
            <HoursSelect
              legend={
                <Stack flexDirection='row' alignItems='center' gap={0.5}>
                  Start time
                  <Tooltip
                    title={
                      <Typography>
                        Set the start time for your daily delivery schedule.
                        This is when the first delivery window begins.
                      </Typography>
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: '255px !important',
                          whiteSpace: 'normal',
                          textAlign: 'left',
                        },
                      },
                    }}
                    arrow
                  >
                    <img
                      src={InfoIcon}
                      alt=''
                      style={{
                        marginBottom: '2px',
                      }}
                    />
                  </Tooltip>
                </Stack>
              }
              timeGap={15}
              value={values.from_time}
              onChange={(e: any) => {
                const { value: from_time } = e.target;
                setFieldValue('from_time', from_time);

                if (isSpecial) {
                  const error = validateSpecialWithWeekdays({
                    ...values,
                    from_time,
                  });
                  setTimeSlotError(error);
                } else {
                  handleSetErrorHours({
                    from: from_time,
                    to: values.to_time,
                    late_night: values.late_night,
                  });
                }
              }}
              error={
                !!getIn(touched, 'from_time') &&
                !!getIn(formErrors, 'from_time')
              }
              helperText={
                getIn(touched, 'from_time') && getIn(formErrors, 'from_time')
              }
            />
            <Stack direction='column' mt={1}>
              <Stack
                direction='row'
                gap={1.6}
                sx={{
                  [themes.breakpoints.down('md')]: {
                    flexDirection: 'column',
                  },
                }}
              >
                <HoursSelect
                  legend={
                    <Stack flexDirection='row' alignItems='center' gap={0.5}>
                      End time
                      <Tooltip
                        title={
                          <Typography>
                            Set the end time for your daily delivery schedule.
                            This is when the final delivery window ends.
                          </Typography>
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: '255px !important',
                              whiteSpace: 'normal',
                              textAlign: 'left',
                            },
                          },
                        }}
                        arrow
                      >
                        <img
                          src={InfoIcon}
                          alt=''
                          style={{
                            marginBottom: '2px',
                          }}
                        />
                      </Tooltip>
                    </Stack>
                  }
                  timeGap={15}
                  value={values.to_time}
                  onChange={(e: any) => {
                    const { value: to_time } = e.target;
                    setFieldValue('to_time', to_time);

                    if (isSpecial) {
                      const error = validateSpecialWithWeekdays({
                        ...values,
                        to_time,
                      });
                      setTimeSlotError(error);
                    } else {
                      handleSetErrorHours({
                        from: values.from_time,
                        to: to_time,
                        late_night: values.late_night,
                      });
                    }
                  }}
                  error={
                    !!getIn(touched, 'to_time') &&
                    !!getIn(formErrors, 'to_time')
                  }
                  helperText={
                    getIn(touched, 'to_time') && getIn(formErrors, 'to_time')
                  }
                />
                <Field.Select
                  fullWidth
                  name='late_night'
                  options={[
                    {
                      label: 'the same day',
                      value: 'same_day',
                    },
                    {
                      label: 'the next day',
                      value: 'next_day',
                    },
                  ]}
                  onChange={(e: any) => {
                    const { value } = e.target;
                    setFieldValue('late_night', value);

                    if (isSpecial) {
                      const error = validateSpecialWithWeekdays({
                        ...values,
                        late_night: value,
                      });
                      setTimeSlotError(error);
                    } else {
                      handleSetErrorHours({
                        from: values.from_time,
                        to: values.to_time,
                        late_night: value,
                      });
                    }
                  }}
                  formControlProps={{
                    sx: {
                      '& > div': {
                        background: 'white',
                      },
                    },
                  }}
                  legend='Late night'
                  sxLabel={{
                    color: 'transparent !important',
                  }}
                />
              </Stack>
              {timeSlotError ? (
                <FormHelperText error sx={{}}>
                  {timeSlotError}
                </FormHelperText>
              ) : null}
            </Stack>
            <Field.Select
              fullWidth
              name='delivery_window'
              options={GAP_TIME_OPTIONS.map((o) => ({
                value: o.value,
                label: `${o.label}`,
              }))}
              formControlProps={{
                sx: {
                  'mt': 1,
                  '& > div': {
                    background: 'white',
                  },
                },
              }}
              legend={
                <Stack flexDirection='row' alignItems='center' gap={0.5}>
                  Delivery slot length
                  <Tooltip
                    title={
                      <Typography>
                        Choose how long each delivery window should last.
                      </Typography>
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: '255px !important',
                          whiteSpace: 'normal',
                          textAlign: 'left',
                        },
                      },
                    }}
                    arrow
                  >
                    <img
                      src={InfoIcon}
                      alt=''
                      style={{
                        marginBottom: '2px',
                      }}
                    />
                  </Tooltip>
                </Stack>
              }
            />
            <Field.Select
              fullWidth
              name='next_available_time'
              options={GAP_TIME_OPTIONS}
              formControlProps={{
                sx: {
                  'mt': 1,
                  '& > div': {
                    background: 'white',
                  },
                },
              }}
              legend={
                <Stack flexDirection='row' alignItems='center' gap={0.5}>
                  Gap between slot start times
                  <Tooltip
                    title={
                      <Typography>
                        Set a buffer between delivery slot start times to
                        prevent back-to-back bookings. For example, a 30-minute
                        gap means a slot from 1–2 p.m would be followed by a
                        1:30–2:30 p.m option.
                      </Typography>
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: '320px !important',
                          whiteSpace: 'normal',
                          textAlign: 'left',
                        },
                      },
                    }}
                    arrow
                  >
                    <img
                      src={InfoIcon}
                      alt=''
                      style={{
                        marginBottom: '2px',
                      }}
                    />
                  </Tooltip>
                </Stack>
              }
            />
            <Stack
              direction='row'
              gap={1.6}
              sx={{
                [themes.breakpoints.down('md')]: {
                  flexDirection: 'column',
                },
              }}
            >
              <Field.Select
                fullWidth
                name='lead_time'
                options={[
                  {
                    value: 0,
                    label: 'None',
                  },
                  ...GAP_TIME_OPTIONS,
                ]}
                formControlProps={{
                  sx: {
                    'mt': 1,
                    '& > div': {
                      background: 'white',
                    },
                  },
                }}
                legend={
                  <Stack flexDirection='row' alignItems='center' gap={0.5}>
                    Order cut-off time (optional){' '}
                    <Tooltip
                      title={
                        <Typography sx={{ whiteSpace: 'break-spaces' }}>
                          Set how far in advance customers must place an order
                          to qualify for a specific delivery window. Orders
                          placed after this lead time will only see later slots.
                        </Typography>
                      }
                      componentsProps={{
                        tooltip: {
                          sx: {
                            maxWidth: '275px !important',
                            whiteSpace: 'normal',
                            textAlign: 'left',
                          },
                        },
                      }}
                      arrow
                    >
                      <img
                        src={InfoIcon}
                        alt=''
                        style={{
                          marginBottom: '2px',
                        }}
                      />
                    </Tooltip>
                  </Stack>
                }
              />
              <Field.Select
                fullWidth
                name='lead_time_type'
                options={[
                  {
                    value: 'start_time',
                    label: 'Before start time',
                  },
                  {
                    value: 'end_time',
                    label: 'Before end time',
                  },
                ]}
                formControlProps={{
                  sx: {
                    'mt': 1,
                    '& > div': {
                      background: 'white',
                    },
                  },
                }}
                sxLabel={{
                  color: 'transparent !important',
                }}
                legend='Type'
              />
            </Stack>
          </Stack>
          {renderPreview()}
        </Stack>
        <Typography
          sx={{
            color: '#F24822',
            fontSize: 12,
            mt: 1.6,
          }}
        >
          Once time slots are updated, the existing order limits will be reset.
        </Typography>
      </>
    );
  };

  const renderCustom = ({
    values,
    setFieldValue,
    setFieldTouched,
    errors: formErrors,
    touched,
  }) => {
    const { time_slot } = values;

    const helperText =
      isArray(getIn(touched, 'time_slot')) &&
      isString(getIn(formErrors, 'time_slot'))
        ? getIn(formErrors, 'time_slot')
        : '';

    return (
      <Stack
        flexDirection='column'
        gap={1.6}
        sx={{
          background: themes.bg.lightPurple,
          borderRadius: '4px',
          p: 1.6,
          mt: 1.6,
        }}
      >
        <Stack direction='column'>
          <Typography color={themes.color.black}>Time slots</Typography>
          <FieldArray name='time_slot'>
            {({ remove, push }) => (
              <Stack>
                <Stack direction='row' gap={1} mt={1.5}>
                  <Grid container spacing={0.8}>
                    <Grid item xs={3}>
                      <Typography color='#8C95BA' fontSize={12}>
                        Start time
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography color='#8C95BA' fontSize={12}>
                        End time
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack
                        flexDirection='row'
                        alignItems='center'
                        gap={0.5}
                        sx={{
                          pb: 1.5,
                        }}
                      >
                        <Typography color='#8C95BA' fontSize={12}>
                          Order cut-off time (optional){' '}
                        </Typography>
                        <Tooltip
                          title={
                            <Typography sx={{ whiteSpace: 'break-spaces' }}>
                              Set how far in advance customers must place an
                              order to qualify for a specific delivery window.
                              Orders placed after this lead time will only see
                              later slots.
                            </Typography>
                          }
                          componentsProps={{
                            tooltip: {
                              sx: {
                                maxWidth: '275px !important',
                                whiteSpace: 'normal',
                                textAlign: 'left',
                              },
                            },
                          }}
                          arrow
                        >
                          <img
                            src={InfoIcon}
                            alt=''
                            style={{
                              marginBottom: '2px',
                            }}
                          />
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </Grid>
                  {time_slot?.length !== 1 && (
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        background: 'transparent',
                      }}
                    />
                  )}
                </Stack>
                {time_slot?.map(
                  (
                    { from_time, to_time, lead_time, lead_time_type },
                    index,
                  ) => (
                    <Stack
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      sx={{
                        [themes.breakpoints.down('md')]: {
                          flexDirection: 'column',
                        },
                        mt: index === 0 ? 0.6 : 1.6,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Grid
                        container
                        spacing={0.8}
                        sx={{
                          '.MuiFormControl-root': {
                            minWidth: 'unset',
                          },
                        }}
                      >
                        <Grid item xs={3}>
                          <HoursSelect
                            legend=''
                            timeGap={15}
                            value={from_time}
                            onChange={(e: any) => {
                              const { value } = e.target;
                              const updatedSlots = [...time_slot];
                              updatedSlots[index] = {
                                ...updatedSlots[index],
                                from_time: value,
                              };
                              setFieldValue(
                                `time_slot[${index}].from_time`,
                                value,
                                true,
                              );
                              setFieldTouched(
                                `time_slot[${index}].from_time`,
                                true,
                                false,
                              );
                              setFieldTouched(
                                `time_slot[${index}].to_time`,
                                true,
                                false,
                              );

                              if (isSpecial) {
                                // For special dates, validate with weekdays
                                const error = validateSpecialWithWeekdays({
                                  ...values,
                                  time_slot: updatedSlots,
                                });
                                setTimeSlotError(error);
                              } else {
                                // For weekdays, validate with adjacent days
                                const error =
                                  validateCustomTimeSlotsWithAdjacentDays(
                                    updatedSlots,
                                  );
                                setTimeSlotError(error);
                              }
                            }}
                            error={
                              !!getIn(
                                touched,
                                `time_slot[${index}].from_time`,
                              ) &&
                              !!getIn(
                                formErrors,
                                `time_slot[${index}].from_time`,
                              )
                            }
                            helperText={
                              getIn(touched, `time_slot[${index}].from_time`) &&
                              getIn(formErrors, `time_slot[${index}].from_time`)
                            }
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <HoursSelect
                            legend=''
                            timeGap={15}
                            value={to_time}
                            onChange={(e: any) => {
                              const { value } = e.target;
                              const updatedSlots = [...time_slot];
                              updatedSlots[index] = {
                                ...updatedSlots[index],
                                to_time: value,
                              };
                              setFieldValue(
                                `time_slot[${index}].to_time`,
                                value,
                                true,
                              );
                              setFieldTouched(
                                `time_slot[${index}].from_time`,
                                true,
                                false,
                              );
                              setFieldTouched(
                                `time_slot[${index}].to_time`,
                                true,
                                false,
                              );

                              if (isSpecial) {
                                // For special dates, validate with weekdays
                                const error = validateSpecialWithWeekdays({
                                  ...values,
                                  time_slot: updatedSlots,
                                });
                                setTimeSlotError(error);
                              } else {
                                // For weekdays, validate with adjacent days
                                const error =
                                  validateCustomTimeSlotsWithAdjacentDays(
                                    updatedSlots,
                                  );
                                setTimeSlotError(error);
                              }
                            }}
                            error={
                              !!getIn(touched, `time_slot[${index}].to_time`) &&
                              !!getIn(formErrors, `time_slot[${index}].to_time`)
                            }
                            helperText={
                              getIn(touched, `time_slot[${index}].to_time`) &&
                              getIn(formErrors, `time_slot[${index}].to_time`)
                            }
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Field.Select
                            fullWidth
                            name={`time_slot[${index}].lead_time`}
                            value={lead_time}
                            onChange={(e: any) => {
                              const { value } = e.target;
                              setFieldValue(
                                `time_slot[${index}].lead_time`,
                                value || 0,
                              );
                            }}
                            options={[
                              {
                                value: 0,
                                label: 'None',
                              },
                              ...GAP_TIME_OPTIONS,
                            ]}
                            formControlProps={{
                              sx: {
                                '& > div': {
                                  background: 'white',
                                },
                              },
                            }}
                            legend=''
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Field.Select
                            fullWidth
                            name={`time_slot[${index}].lead_time_type`}
                            value={lead_time_type}
                            onChange={(e: any) => {
                              const { value } = e.target;
                              setFieldValue(
                                `time_slot[${index}].lead_time_type`,
                                value,
                              );
                            }}
                            options={[
                              {
                                value: 'start_time',
                                label: 'Before start time',
                              },
                              {
                                value: 'end_time',
                                label: 'Before end time',
                              },
                            ]}
                            formControlProps={{
                              sx: {
                                '& > div': {
                                  background: 'white',
                                },
                              },
                            }}
                            legend=''
                            sxLabel={{
                              color: 'transparent !important',
                            }}
                          />
                        </Grid>
                      </Grid>
                      {time_slot?.length !== 1 && (
                        <Stack
                          onClick={() => {
                            const updatedSlots = time_slot.filter(
                              (_, i) => i !== index,
                            );
                            remove(index);

                            if (isSpecial) {
                              const error = validateSpecialWithWeekdays({
                                ...values,
                                time_slot: updatedSlots,
                              });
                              setTimeSlotError(error);
                            } else {
                              const error =
                                validateCustomTimeSlotsWithAdjacentDays(
                                  updatedSlots,
                                );
                              setTimeSlotError(error);
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            pt: 0.8,
                          }}
                          flexDirection='column'
                        >
                          <img
                            src={DeleteIcon}
                            alt=''
                            style={{
                              width: '16px',
                              height: '16px',
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  ),
                )}
                <Stack flexDirection='row' gap={1.6}>
                  <Button
                    noRounder
                    buttonType='default'
                    sx={{
                      mt: 2.5,
                      gap: '4px',
                      width: 'fit-content',
                    }}
                    onClick={() => {
                      push(defaultCustomizeTimeSlot);
                      const updatedSlots = [
                        ...time_slot,
                        defaultCustomizeTimeSlot,
                      ];

                      if (isSpecial) {
                        const error = validateSpecialWithWeekdays({
                          ...values,
                          time_slot: updatedSlots,
                        });
                        setTimeSlotError(error);
                      } else {
                        const error =
                          validateCustomTimeSlotsWithAdjacentDays(updatedSlots);
                        setTimeSlotError(error);
                      }
                    }}
                  >
                    <img src={AddIcon} alt='' />
                    Add Slot
                  </Button>
                </Stack>
              </Stack>
            )}
          </FieldArray>
          {helperText && <FormHelperText error>{helperText}</FormHelperText>}
          {timeSlotError && (
            <FormHelperText error sx={{ mt: 1 }}>
              {timeSlotError}
            </FormHelperText>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderNone = ({
    values,
    setFieldValue,
    touched,
    errors: formErrors,
  }) => (
    <Stack mt={1.6}>
      <HoursSelect
        legend='Last Order Time'
        timeGap={15}
        value={values.last_order_time}
        onChange={(e: any) => {
          const { value: last_order_time } = e.target;
          setFieldValue('last_order_time', last_order_time);
        }}
        error={
          !!getIn(touched, 'last_order_time') &&
          !!getIn(formErrors, 'last_order_time')
        }
        helperText={
          getIn(touched, 'last_order_time') &&
          getIn(formErrors, 'last_order_time')
        }
      />
    </Stack>
  );

  const renderEnableDelivery = ({
    values,
    setFieldValue,
    setFieldTouched,
    errors: formErrors,
    touched,
  }) => {
    const { time_slot_generation } = values;

    let contentComponent;
    switch (time_slot_generation) {
      case TIME_SLOT_TYPE.AUTO: {
        contentComponent = renderGenerate({
          values,
          setFieldValue,
          errors: formErrors,
          touched,
        });
        break;
      }

      case TIME_SLOT_TYPE.CUSTOM: {
        contentComponent = renderCustom({
          values,
          setFieldValue,
          setFieldTouched,
          errors: formErrors,
          touched,
        });
        break;
      }

      case TIME_SLOT_TYPE.NONE: {
        contentComponent = renderNone({
          values,
          setFieldValue,
          errors: formErrors,
          touched,
        });
        break;
      }

      default:
        break;
    }

    return (
      <>
        <Stack flexDirection='column' gap={1}>
          <Typography color={themes.color.violet500}>Availability</Typography>
          <Stack flexDirection='row' alignItems='center'>
            <Field.SwitchButton noText name='available' />
            Allow customers to choose a time slot.
          </Stack>
        </Stack>
        {values.available && (
          <>
            <Stack mt={1.6}>
              <Field.Select
                fullWidth
                name='time_slot_generation'
                options={TIME_SLOT_OPTIONS.map((o) => ({
                  value: o.value,
                  label: `${o.label}`,
                }))}
                formControlProps={{
                  sx: { width: '100%', mt: 1 },
                }}
                onChange={(e: any) => {
                  const newType = e.target.value;
                  // Reset or re-validate based on new type
                  if (newType === TIME_SLOT_TYPE.CUSTOM && values.time_slot) {
                    // Re-validate custom time slots
                    const error = validateCustomTimeSlotsWithAdjacentDays(
                      values.time_slot,
                    );
                    setTimeSlotError(error);
                  } else if (newType === TIME_SLOT_TYPE.AUTO) {
                    if (isSpecial) {
                      // Validate special with weekdays
                      const error = validateSpecialWithWeekdays({
                        ...values,
                        time_slot_generation: newType,
                      });
                      setTimeSlotError(error);
                    } else {
                      // Validate auto settings for weekday
                      handleSetErrorHours({
                        from: values.from_time,
                        to: values.to_time,
                        late_night: values.late_night,
                      });
                    }
                  } else {
                    // For NONE type, clear error
                    setTimeSlotError('');
                  }
                }}
                legend='Time slot option'
              />
            </Stack>
            {contentComponent}
          </>
        )}
      </>
    );
  };

  return (
    <Modal
      title={title}
      open={open}
      onClose={onClose}
      disableCloseOutside
      PaperProps={{
        sx: {
          width: 740,
        },
      }}
      maxWidth='lg'
      actions={[
        {
          title: 'Cancel',
          onClick: () => onClose(),
          buttonType: 'default',
        },
        {
          title: 'Save',
          buttonType: 'primary',
          onClick: () => formRef.current?.handleSubmit(),
          loading: isLoading,
          disabled:
            ((currentTimeSlotGeneration === TIME_SLOT_TYPE.AUTO ||
              currentTimeSlotGeneration === TIME_SLOT_TYPE.CUSTOM) &&
              !!timeSlotError) ||
            (currentTimeSlotGeneration === TIME_SLOT_TYPE.CUSTOM &&
              hasFormikErrors) ||
            (isSpecial && hasDateErrors) ||
            (isSpecial && !!dateError) ||
            (!dirtyForm && !isBulkSelect),
        },
      ]}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
        innerRef={formRef}
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          errors: formErrors,
          touched,
          dirty,
        }) => {
          setDirtyForm(dirty);
          setCurrentTimeSlotGeneration(values.time_slot_generation);

          // Check if there are validation errors for CUSTOM mode
          const hasTimeSlotErrors =
            values.time_slot_generation === TIME_SLOT_TYPE.CUSTOM &&
            formErrors.time_slot &&
            (isArray(formErrors.time_slot)
              ? formErrors.time_slot.some((err) => !!err)
              : isString(formErrors.time_slot));

          setHasFormikErrors(!!hasTimeSlotErrors);

          // Check if there are validation errors for date fields when isSpecial
          if (isSpecial) {
            const hasDateFieldErrors =
              !!formErrors.start_date || !!formErrors.end_date;
            setHasDateErrors(hasDateFieldErrors);
          }

          // Store current form values and detect available changes
          currentFormValuesRef.current = values;

          // Watch for available changes and trigger validation
          if (
            prevAvailableRef.current !== undefined &&
            prevAvailableRef.current !== values.available &&
            values.available === true
          ) {
            // Trigger validation via state change
            setAvailableChangeTrigger((prev) => prev + 1);
          } else if (
            prevAvailableRef.current !== undefined &&
            prevAvailableRef.current !== values.available &&
            values.available === false
          ) {
            // Clear error when switching to false
            setTimeSlotError('');
          }

          // Update ref after comparison
          prevAvailableRef.current = values.available;

          return (
            <Form
              style={{ minHeight: 520, color: themes.color.violet500 }}
              onKeyDown={(e) => {
                if (e.code === 'Enter') {
                  e.preventDefault();
                }
              }}
            >
              {isSpecial && (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Field.TextInput name='name' legend='Name' />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field.DateSelector
                        legend='Start date'
                        name='start_date'
                        dateFormat
                        disablePastDate
                        timeFormat={false}
                        invalidDays={invalidDays}
                        onApply={(_, val) => {
                          const newStartDate = moment(val)
                            .local()
                            .format('YYYY-MM-DD');
                          setFieldValue('start_date', newStartDate);

                          // Validate date overlap and time conflicts when date changes
                          if (values.end_date) {
                            const updatedValues = {
                              ...values,
                              start_date: newStartDate,
                            };
                            // Check date overlap
                            const dateOverlapError =
                              validateDateOverlap(updatedValues);
                            setDateError(dateOverlapError);
                            // Check time conflicts
                            const error =
                              validateSpecialWithWeekdays(updatedValues);
                            setTimeSlotError(error);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field.DateSelector
                        name='end_date'
                        legend='End date'
                        dateFormat
                        disablePastDate
                        invalidDays={invalidDays}
                        onApply={(_, val) => {
                          const newEndDate = moment(val)
                            .local()
                            .format('YYYY-MM-DD');
                          setFieldValue('end_date', newEndDate);

                          // Validate date overlap and time conflicts when date changes
                          if (values.start_date) {
                            const updatedValues = {
                              ...values,
                              end_date: newEndDate,
                            };
                            // Check date overlap
                            const dateOverlapError =
                              validateDateOverlap(updatedValues);
                            setDateError(dateOverlapError);
                            // Check time conflicts
                            const error =
                              validateSpecialWithWeekdays(updatedValues);
                            setTimeSlotError(error);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                  {dateError ? (
                    <FormHelperText error>{dateError}</FormHelperText>
                  ) : null}
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              {enableDelivery
                ? renderEnableDelivery({
                    values,
                    setFieldValue,
                    setFieldTouched,
                    errors: formErrors,
                    touched,
                  })
                : renderDisableDelivery({
                    values,
                    setFieldValue,
                    errors: formErrors,
                    touched,
                  })}
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
